'use client'

import cx from 'classnames'
import { FC, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import ErrorMessage from '@/components/Form/Atoms/ErrorMessage'
import '@/components/Form/Atoms/InputBase/InputBase.scss'
import RequiredLabel from '@/components/Form/Atoms/RequiredLabel'
import { useEvent } from '@/components/hooks/useRecordEvent'

type Props = {
  name: string
  label?: string | null
  autoComplete?: string | null
  placeholder?: string | null
  readOnly?: boolean
  forceShowErrorMessage?: boolean
  required?: boolean
  width?: string
  height?: string
  minWidth?: string
  minHeight?: string
  maxWidth?: string
  maxHeight?: string
  help?: string | null
  limitRegExp?: RegExp | null
  rows?: number
}

const TextareaBase: FC<Props> = ({
  name,
  label = null,
  autoComplete = null,
  placeholder = null,
  readOnly = false,
  forceShowErrorMessage = false,
  required = false,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  help = '',
  limitRegExp = null,
  rows = 5,
}) => {
  const {
    register,
    formState: { errors, dirtyFields },
    setValue,
  } = useFormContext()

  const style = useMemo(() => {
    return {
      width: width,
      height: height,
      minWidth: minWidth,
      minHeight: minHeight,
      maxWidth: maxWidth,
      maxHeight: maxHeight,
    }
  }, [width, height, minWidth, minHeight, maxWidth, maxHeight])

  const { record } = useEvent()

  const showError =
    name.split('.').reduce((result, key) => {
      if (result == null) return null
      const nextResult = result[key]
      return nextResult == undefined ? null : nextResult
    }, dirtyFields) || forceShowErrorMessage

  return (
    <div className="c-inputBase" style={style}>
      <label htmlFor={name}>
        <span
          className={cx('c-inputBase__label', {
            'u-mb14': label,
          })}
        >
          {label}
          {label != null && required && <RequiredLabel marginLeft="1rem" />}
        </span>

        {help && (
          <p className="u-text-xs-2 u-mb14" style={{ color: '#060725', opacity: 0.5 }}>
            {help}
          </p>
        )}

        <textarea
          {...register(name, {
            onChange: (e) => {
              if (limitRegExp != null) {
                setValue(name, e.target.value.replace(limitRegExp, ''))
              }
            },
          })}
          onFocus={() => record('focus', { name })}
          {...(autoComplete != null ? { autoComplete } : {})}
          {...(placeholder != null ? { placeholder } : {})}
          id={name}
          required={required}
          className={cx('u-font-yu-go', 'c-inputBase__textarea')}
          readOnly={readOnly}
          rows={rows}
        />
      </label>
      <div className="u-mt8">{showError ? <ErrorMessage errors={errors} name={name} /> : <></>}</div>
    </div>
  )
}

export default TextareaBase
